import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import monitoringService from '../services/monitoringService'

const CrawlerManagement = () => {
  const { t } = useTranslation()
  const [health, setHealth] = useState(null)
  const [requests, setRequests] = useState({ active: [], completed: [] })
  const [browsers, setBrowsers] = useState({ browsers: [], summary: {} })
  const [janitorLogs, setJanitorLogs] = useState([])
  const [errorLogs, setErrorLogs] = useState([])
  const [endpointStats, setEndpointStats] = useState({})
  const [timeline, setTimeline] = useState({ values: [], timestamps: [] })
  const [timelineMetric, setTimelineMetric] = useState('memory')
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [filterRequests, setFilterRequests] = useState('all')

  useEffect(() => {
    fetchAllData()
    
    if (autoRefresh) {
      // 8s instead of 2s to reduce Chrome memory/CPU load and avoid crashes
      const interval = setInterval(() => {
        fetchAllData()
      }, 8000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const fetchAllData = async () => {
    try {
      const [healthRes, requestsRes, browsersRes, janitorRes, errorsRes, statsRes, timelineRes] = await Promise.all([
        monitoringService.getHealth().catch(e => ({ data: null })),
        monitoringService.getRequests().catch(e => ({ data: { active: [], completed: [] } })),
        monitoringService.getBrowsers().catch(e => ({ data: { browsers: [], summary: {} } })),
        monitoringService.getJanitorLogs(50).catch(e => ({ data: [] })),
        monitoringService.getErrorLogs(50).catch(e => ({ data: [] })),
        monitoringService.getEndpointStats().catch(e => ({ data: {} })),
        fetch('/api/monitoring/timeline?minutes=5').then(r => r.json()).catch(e => ({ data: { values: [], timestamps: [] } }))
      ])

      setHealth(healthRes.data)
      setRequests(requestsRes.data)
      setBrowsers(browsersRes.data)
      setJanitorLogs(Array.isArray(janitorRes.data) ? janitorRes.data : (janitorRes.data?.data || []))
      setErrorLogs(Array.isArray(errorsRes.data) ? errorsRes.data : (errorsRes.data?.data || []))
      setEndpointStats(statsRes.data)
      setTimeline(timelineRes.data || { values: [], timestamps: [] })
      setLoading(false)
    } catch (error) {
      console.error('Error fetching monitoring data:', error)
    }
  }

  const handleForceCleanup = async () => {
    if (!window.confirm(t('monitoring.toast.cleanupConfirm'))) return
    
    try {
      await monitoringService.forceCleanup()
      toast.success(t('monitoring.toast.cleanupTriggered'))
      fetchAllData()
    } catch (error) {
      toast.error(t('monitoring.toast.cleanupFailed'))
    }
  }

  const handleResetStats = async () => {
    if (!window.confirm(t('monitoring.toast.resetStatsConfirm'))) return
    
    try {
      await monitoringService.resetStats()
      toast.success(t('monitoring.toast.statsReset'))
      fetchAllData()
    } catch (error) {
      toast.error(t('monitoring.toast.resetFailed'))
    }
  }

  const handleKillBrowser = async (browserId) => {
    if (!window.confirm(t('monitoring.toast.killBrowserConfirm', { id: browserId }))) return
    
    try {
      await monitoringService.killBrowser(browserId)
      toast.success(t('monitoring.toast.browserKilled', { id: browserId }))
      fetchAllData()
    } catch (error) {
      toast.error(t('monitoring.toast.killFailed'))
    }
  }

  const formatUptime = (seconds) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return `${h}h ${m}m`
  }

  const formatSeconds = (seconds) => {
    if (seconds < 60) return `${seconds}s`
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}m ${s}s`
  }

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin h-12 w-12">
          <i className="fa-solid fa-spinner text-primary-500 text-4xl"></i>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card border-l-4 border-l-primary-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-dark-300">{t('monitoring.title')}</h2>
              <p className="text-dark-100">{t('monitoring.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-md ${
                autoRefresh 
                  ? 'gradient-primary text-white' 
                  : 'bg-light-100 text-dark-300'
              }`}
            >
              <i className={`fa-solid ${autoRefresh ? 'fa-bolt' : 'fa-pause'} mr-2`}></i>
              {t('monitoring.autoRefresh')} {autoRefresh ? t('monitoring.on') : t('monitoring.off')}
            </button>
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold transition-all"
            >
              <i className="fa-solid fa-rotate mr-2"></i>
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* System Health */}
      {health && (
        <div className="card">
          <h3 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
            <i className="fa-solid fa-heart-pulse text-primary-500 mr-2"></i>
            {t('monitoring.systemHealth.title')}
          </h3>
          <div className="grid grid-cols-4 gap-4 mb-6">
            {/* CPU */}
            <div className="p-4 bg-light-100 rounded-lg border border-light-200/30">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-microchip text-primary-500"></i>
                  <span className="text-sm font-semibold text-dark-100">{t('monitoring.systemHealth.cpu')}</span>
                </div>
                <span className="font-bold text-dark-300">{health.container.cpu_percent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-white rounded-full h-3 shadow-inner">
                <div 
                  className={`h-3 rounded-full transition-all ${
                    health.container.cpu_percent > 80 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                    health.container.cpu_percent > 60 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 
                    'bg-gradient-to-r from-green-500 to-green-600'
                  }`}
                  style={{ width: `${Math.min(health.container.cpu_percent, 100)}%` }}
                />
              </div>
            </div>

            {/* Memory */}
            <div className="p-4 bg-light-100 rounded-lg border border-light-200/30">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-memory text-purple-500"></i>
                  <span className="text-sm font-semibold text-dark-100">{t('monitoring.systemHealth.memory')}</span>
                </div>
                <span className="font-bold text-dark-300">{health.container.memory_percent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-white rounded-full h-3 shadow-inner">
                <div 
                  className={`h-3 rounded-full transition-all ${
                    health.container.memory_percent > 80 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                    health.container.memory_percent > 60 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 
                    'bg-gradient-to-r from-purple-500 to-purple-600'
                  }`}
                  style={{ width: `${Math.min(health.container.memory_percent, 100)}%` }}
                />
              </div>
            </div>

            {/* Network */}
            <div className="p-4 bg-light-100 rounded-lg border border-light-200/30">
              <div className="flex items-center gap-2 mb-2">
                <i className="fa-solid fa-network-wired text-primary-800"></i>
                <span className="text-sm font-semibold text-dark-100">{t('monitoring.systemHealth.network')}</span>
              </div>
              <div className="text-sm text-dark-300">
                <div className="flex justify-between mb-1">
                  <span className="text-dark-100">↑ {t('monitoring.systemHealth.sent')}:</span>
                  <span className="font-bold">{health.container.network_sent_mb.toFixed(1)} MB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-100">↓ {t('monitoring.systemHealth.recv')}:</span>
                  <span className="font-bold">{health.container.network_recv_mb.toFixed(1)} MB</span>
                </div>
              </div>
            </div>

            {/* Uptime */}
            <div className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg border border-primary-200">
              <div className="flex items-center gap-2 mb-2">
                <i className="fa-solid fa-clock text-primary-700"></i>
                <span className="text-sm font-semibold text-primary-900">{t('monitoring.systemHealth.uptime')}</span>
              </div>
              <div className="text-2xl font-bold text-primary-800">{formatUptime(health.container.uptime_seconds)}</div>
              <div className="text-xs text-primary-700 mt-1">
                {t('monitoring.systemHealth.updated')}: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>

          {/* Pool Status */}
          <div className="border-t border-light-200/20 pt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa-solid fa-fire text-red-500 text-xl"></i>
                  <span className="text-sm font-bold text-dark-300">{t('monitoring.pool.permanent')}</span>
                </div>
                <div className={`text-lg font-bold ${health.pool.permanent.active ? 'text-green-600' : 'text-gray-400'}`}>
                  {health.pool.permanent.active ? t('monitoring.pool.active') : t('monitoring.pool.inactive')}
                </div>
                <div className="text-xs text-dark-100">{health.pool.permanent.memory_mb}MB</div>
              </div>
              <div className="p-4 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa-solid fa-temperature-hot text-orange-500 text-xl"></i>
                  <span className="text-sm font-bold text-dark-300">{t('monitoring.pool.hot')}</span>
                </div>
                <div className="text-lg font-bold text-orange-600">{health.pool.hot.count} {t('monitoring.pool.browsers')}</div>
                <div className="text-xs text-dark-100">{health.pool.hot.memory_mb}MB</div>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa-solid fa-snowflake text-blue-500 text-xl"></i>
                  <span className="text-sm font-bold text-dark-300">{t('monitoring.pool.cold')}</span>
                </div>
                <div className="text-lg font-bold text-blue-600">{health.pool.cold.count} {t('monitoring.pool.browsers')}</div>
                <div className="text-xs text-dark-100">{health.pool.cold.memory_mb}MB</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live Activity Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Requests Section */}
        <div className="card max-h-[500px] overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-dark-300 flex items-center">
              <i className="fa-solid fa-list-check text-primary-500 mr-2"></i>
              {t('monitoring.requests.title')} ({requests.active.length} {t('monitoring.requests.active')})
            </h3>
            <select
              value={filterRequests}
              onChange={(e) => setFilterRequests(e.target.value)}
              className="px-3 py-1 border border-light-200/30 rounded-lg text-sm font-medium"
            >
              <option value="all">{t('monitoring.requests.all')}</option>
              <option value="success">{t('monitoring.requests.success')}</option>
              <option value="error">{t('monitoring.requests.errors')}</option>
            </select>
          </div>
          
          <div className="space-y-3 overflow-auto max-h-[400px]">
            {requests.active.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-dark-100 mb-2 uppercase tracking-wide">{t('monitoring.requests.activeRequests')}</h4>
                {requests.active.map((req, idx) => (
                  <div key={idx} className="p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-300 text-sm mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-primary-600 bg-white px-2 py-1 rounded">{req.id?.substring(0, 8)}</span>
                      <span className="text-dark-300 font-semibold">{req.endpoint}</span>
                      <span className="text-orange-600 font-bold flex items-center">
                        <i className="fa-solid fa-clock animate-pulse mr-1"></i>
                        {req.elapsed?.toFixed(1)}s
                      </span>
                    </div>
                    <div className="text-xs text-dark-100 truncate">{req.url}</div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <h4 className="text-sm font-bold text-dark-100 mb-2 uppercase tracking-wide">{t('monitoring.requests.recentCompleted')}</h4>
              {requests.completed.length === 0 ? (
                <div className="text-center py-6 bg-light-100 rounded-lg">
                  <i className="fa-solid fa-inbox text-light-200 text-3xl mb-2"></i>
                  <p className="text-dark-100 text-sm">{t('monitoring.requests.noCompleted')}</p>
                </div>
              ) : (
                requests.completed.slice(0, 10).map((req, idx) => (
                  <div key={idx} className="p-2 bg-light-100 rounded-lg border border-light-200/30 text-xs mb-1 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-dark-100 bg-white px-2 py-0.5 rounded">{req.id?.substring(0, 8)}</span>
                      <span className="text-dark-100 w-24 truncate">{req.endpoint}</span>
                      <span className="text-dark-300 truncate flex-1" title={req.url}>{req.url}</span>
                      <span className="text-dark-100 font-semibold">{req.elapsed?.toFixed(2)}s</span>
                      <span className={`font-semibold ${req.mem_delta > 0 ? 'text-orange-600' : 'text-dark-100'}`}>
                        {req.mem_delta > 0 ? '+' : ''}{req.mem_delta}MB
                      </span>
                      <span className="font-bold">{req.success ? '✅' : '❌'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Browsers Section */}
        <div className="card max-h-[500px] overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-dark-300 flex items-center">
              <i className="fa-brands fa-chrome text-primary-500 mr-2"></i>
              {t('monitoring.browsers.title')} ({browsers.summary.total_count || 0})
            </h3>
            <div className="text-sm">
              <span className="text-dark-100">{t('monitoring.browsers.reuseRate')}:</span>
              <span className="ml-2 font-bold text-primary-600">
                {browsers.summary.reuse_rate_percent?.toFixed(1) || 0}%
              </span>
            </div>
          </div>
          
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-light-100 to-light-100/50 sticky top-0">
                <tr className="text-left text-dark-100">
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.type')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.signature')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.age')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.lastUsed')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.hits')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.memory')}</th>
                  <th className="px-3 py-2 font-bold">{t('monitoring.browsers.action')}</th>
                </tr>
              </thead>
              <tbody>
                {browsers.browsers?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-dark-100">
                      <i className="fa-brands fa-chrome text-light-200 text-4xl mb-2"></i>
                      <p>{t('monitoring.browsers.noBrowsers')}</p>
                    </td>
                  </tr>
                ) : (
                  browsers.browsers?.map((browser, idx) => {
                    const typeIcon = browser.type === 'permanent' ? 'fa-fire' : browser.type === 'hot' ? 'fa-temperature-hot' : 'fa-snowflake'
                    const typeColor = browser.type === 'permanent' ? 'text-red-600' : browser.type === 'hot' ? 'text-orange-600' : 'text-blue-600'
                    const bgColor = browser.type === 'permanent' ? 'bg-red-50' : browser.type === 'hot' ? 'bg-orange-50' : 'bg-blue-50'
                    
                    return (
                      <tr key={idx} className="border-t border-light-200/30 hover:bg-light-100/50 transition-colors">
                        <td className="px-3 py-3">
                          <span className={`${typeColor} ${bgColor} px-2 py-1 rounded-lg font-bold text-xs inline-flex items-center gap-1`}>
                            <i className={`fa-solid ${typeIcon}`}></i>
                            {browser.type}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-mono text-xs bg-light-100 rounded">{browser.sig}</td>
                        <td className="px-3 py-3 text-dark-300 font-semibold">{formatSeconds(browser.age_seconds || 0)}</td>
                        <td className="px-3 py-3 text-dark-100">{formatSeconds(browser.last_used_seconds || 0)}</td>
                        <td className="px-3 py-3 text-dark-300 font-bold">{browser.hits}</td>
                        <td className="px-3 py-3 text-dark-300 font-semibold">{browser.memory_mb}MB</td>
                        <td className="px-3 py-3">
                          {browser.killable ? (
                            <button
                              onClick={() => handleKillBrowser(browser.sig)}
                              className="text-red-500 hover:text-red-700 font-bold bg-red-50 px-2 py-1 rounded hover:bg-red-100 transition-colors"
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          ) : (
                            <span className="text-light-200 text-xs bg-light-100 px-2 py-1 rounded">{t('monitoring.browsers.locked')}</span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Janitor & Errors Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Janitor Events */}
        <div className="card max-h-80 overflow-hidden">
          <h3 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
            <i className="fa-solid fa-broom text-primary-500 mr-2"></i>
            {t('monitoring.janitor.title')}
          </h3>
          <div className="space-y-2 overflow-auto max-h-64">
            {janitorLogs.length === 0 ? (
              <div className="text-center py-6 bg-light-100 rounded-lg">
                <i className="fa-solid fa-broom text-light-200 text-3xl mb-2"></i>
                <p className="text-dark-100 text-sm">{t('monitoring.janitor.noEvents')}</p>
              </div>
            ) : (
              janitorLogs.slice(0, 20).map((evt, idx) => (
                <div key={idx} className="p-3 bg-light-100 rounded-lg border border-light-200/30 font-mono text-xs hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-dark-100 bg-white px-2 py-0.5 rounded">
                      {new Date(evt.timestamp * 1000).toLocaleTimeString()}
                    </span>
                    <span className="text-lg">{evt.type === 'close_cold' ? '🧹❄️' : evt.type === 'close_hot' ? '🧹♨️' : '⬆️'}</span>
                    <span className="text-primary-600 font-bold">{evt.type}</span>
                    <span className="text-dark-100">sig={evt.sig}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Error Logs */}
        <div className="card max-h-80 overflow-hidden">
          <h3 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
            <i className="fa-solid fa-triangle-exclamation text-red-500 mr-2"></i>
            {t('monitoring.errors.title')}
          </h3>
          <div className="space-y-2 overflow-auto max-h-64">
            {errorLogs.length === 0 ? (
              <div className="text-center py-6 bg-green-50 rounded-lg border border-green-200">
                <i className="fa-solid fa-check-circle text-green-500 text-3xl mb-2"></i>
                <p className="text-green-700 text-sm font-semibold">{t('monitoring.errors.noErrors')}</p>
              </div>
            ) : (
              errorLogs.slice(0, 20).map((err, idx) => (
                <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-300 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-dark-100 bg-white px-2 py-1 rounded">
                      {new Date(err.timestamp * 1000).toLocaleTimeString()}
                    </span>
                    <span className="text-red-600 font-bold text-xs">{err.endpoint}</span>
                  </div>
                  <div className="text-xs text-dark-100 truncate mb-1">{err.url}</div>
                  <div className="text-xs text-red-700 font-mono bg-white px-2 py-1 rounded">{err.error}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Endpoint Analytics */}
      <div className="card">
        <h3 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
          <i className="fa-solid fa-chart-line text-primary-500 mr-2"></i>
          {t('monitoring.analytics.title')}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-light-100 to-light-100/50">
              <tr className="text-left text-dark-100">
                <th className="px-4 py-3 font-bold">{t('monitoring.analytics.endpoint')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('monitoring.analytics.count')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('monitoring.analytics.avgLatency')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('monitoring.analytics.successRate')}</th>
                <th className="px-4 py-3 text-right font-bold">{t('monitoring.analytics.poolHitRate')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(endpointStats).length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-dark-100">
                    <i className="fa-solid fa-chart-simple text-light-200 text-4xl mb-2"></i>
                    <p>{t('monitoring.analytics.noData')}</p>
                  </td>
                </tr>
              ) : (
                Object.entries(endpointStats).map(([endpoint, stats], idx) => (
                  <tr key={idx} className="border-t border-light-200/30 hover:bg-light-100/50 transition-colors">
                    <td className="px-4 py-3 text-primary-600 font-semibold">{endpoint}</td>
                    <td className="px-4 py-3 text-right font-bold text-dark-300">{stats.count}</td>
                    <td className="px-4 py-3 text-right text-dark-300">{stats.avg_latency_ms}ms</td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      stats.success_rate_percent >= 99 ? 'text-green-600' : 
                      stats.success_rate_percent >= 90 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {stats.success_rate_percent.toFixed(1)}%
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      stats.pool_hit_rate_percent >= 90 ? 'text-green-600' : 
                      stats.pool_hit_rate_percent >= 70 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {stats.pool_hit_rate_percent.toFixed(1)}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Control Actions */}
      <div className="card">
        <h3 className="text-xl font-bold text-dark-300 mb-4 flex items-center">
          <i className="fa-solid fa-gamepad text-primary-500 mr-2"></i>
          {t('monitoring.controls.title')}
        </h3>
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={handleForceCleanup}
            className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold shadow-lg transition-all"
          >
            <i className="fa-solid fa-broom mr-2"></i>
            {t('monitoring.controls.forceCleanup')}
          </button>
          <button
            onClick={handleResetStats}
            className="px-6 py-3 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold transition-all"
          >
            <i className="fa-solid fa-chart-simple mr-2"></i>
            {t('monitoring.controls.resetStats')}
          </button>
        </div>
        <div className="bg-primary-50 border-l-4 border-primary-500 rounded-r-lg p-4">
          <p className="text-sm text-dark-300">
            <i className="fa-solid fa-info-circle text-primary-500 mr-2"></i>
            <span className="font-semibold">{t('monitoring.controls.note')}:</span> {t('monitoring.controls.noteText')}
          </p>
        </div>
      </div>
    </div>
  )
}

export default CrawlerManagement
